import React, {
    useCallback,
    useContext,
    useMemo,
    useRef,
    useState,
} from "react";
import { useDefaultLayout } from "react-resizable-panels";
import {
    FinancialDocumentExtended,
    ImporterErrors,
} from "@common/types/financialDocument";
import { optionalCounterpartyEmailSchema } from "@common/schemas/counterparty.schema";
import { InvoiceExtractionLineItem } from "@common/schemas/financialDocument/invoice-extraction-line-item.schema";
import { InvoiceExtractionRecord } from "@common/schemas/financialDocument/invoice-extraction-record.schema";
import { Entity } from "@common/types/entity";
import {
    ResizableHandle,
    ResizablePanel,
    ResizablePanelGroup,
} from "@design-system/ResizablePanels/ResizablePanels";
import { useKeyboardCommand } from "@hooks/keyboard/useKeyboardCommand";
import { fromStateFromEntity } from "@components/invoices/NativeInvoiceDrawer/copyNativeInvoicePartyDetails";
import { createNativeInvoiceFormState } from "@components/invoices/NativeInvoiceDrawer/createNativeInvoiceFormState";
import { ConfirmDiscardChangesModal } from "@components/invoices/NativeInvoiceDrawer/ConfirmDiscardChangesModal";
import { InvoiceDocumentPreview } from "@components/invoices/NativeInvoiceDrawer/InvoiceDocumentPreview";
import { NativeInvoiceFormProvider } from "@components/invoices/NativeInvoiceDrawer/NativeInvoiceFormProvider";
import { useNativeInvoiceForm } from "@components/invoices/NativeInvoiceDrawer/useNativeInvoiceForm";
import { getNativeInvoiceDueDateError } from "@components/invoices/NativeInvoiceDrawer/nativeInvoiceFormHelpers";
import {
    NativeInvoiceCustomer,
    NativeInvoiceLineRow,
} from "@components/invoices/NativeInvoiceDrawer/nativeInvoiceFormTypes";
import { FinancialDocumentPreview } from "@components/documents/FinancialDocumentDetails/FinancialDocumentPreview";
import { useDownloadFinancialDocumentFile } from "@components/documents/FinancialDocumentDetails/useDownloadFinancialDocumentFile";
import importerStyles from "@components/importer/ImporterPage.module.scss";
import {
    batchUpdateBillExtractionRows,
    batchUpdateInvoiceExtractionRows,
    useRemoveImporterErrorMutation,
} from "@api/financialDocument.api";
import styles from "@components/invoices/NativeInvoiceDrawer/NativeInvoiceDrawer.module.scss";
import { AccrualImporterContext } from "../accrualImporter.context";
import { AccrualDocumentImporterMode } from "../accrualDocumentImporter.lib";
import { isAccrualAccountSuggestion } from "../accrualSuggestion";
import { buildAccrualCounterpartyCreatePrefill } from "../buildCounterpartyPrefillFromPartyHints";
import {
    AccrualDraftRequiredField,
    accrualDraftMissingFields,
    applyInvoiceExtractionToRawState,
} from "./accrualDraftInvoiceMapping";
import { accrualDraftCopy } from "./accrualDraftCopy";
import { useCatalogItemSuggestions } from "./useCatalogItemSuggestions";
import { AccrualDraftDrawerShell } from "./AccrualDraftDrawerShell";
import { AccrualImporterDocumentRow } from "../accrualImporter.types";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { AccrualDraftCommandBar } from "./AccrualDraftCommandBar";
import { AccrualDraftDetailsSections } from "./AccrualDraftDetailsSections";
import { AccrualDraftCounterpartyDetailChanges } from "./AccrualDraftCounterpartyDetailChanges";
import { useAccrualCounterpartyDetailChanges } from "./useAccrualCounterpartyDetailChanges";
import {
    getSuggestedOwnPartyEntity,
    getUnmatchedParsedEntityName,
    unmatchedEntityReviewTaskText,
} from "../accrualReviewTasks";
import {
    getDisplayErrorMessage,
    ImporterDisplayErrorKind,
} from "@components/importer/helpers";
import {
    useConfirmAccrualDraft,
    useSaveAccrualDraftCustomer,
    useSaveAccrualDraftDetails,
} from "./useConfirmAccrualDraft";
import { useAssignCounterpartyToMatchingDrafts } from "../useAssignCounterpartyToMatchingDrafts";
import {
    remapLineAccountsToEntity,
    useEntitySwitchAccounts,
} from "./remapLineAccountsToEntity";

function accountSuggestionUpsert({
    lineItem,
    rowIndex,
    formLines,
}: {
    lineItem: InvoiceExtractionLineItem;
    rowIndex: number;
    formLines: NativeInvoiceLineRow[];
}) {
    const formLine = formLines.find((line) => line.id === lineItem.lineId);
    return {
        rowIndex,
        description: lineItem.description,
        amount: lineItem.amount,
        quantity: lineItem.quantity,
        unitPrice: lineItem.unitPrice,
        classIds: lineItem.classIds,
        accountId: formLine?.accrualAccountId ?? lineItem.accountId,
        accrualAccountId:
            formLine?.receivablesAccountId ?? lineItem.accrualAccountId,
        acceptAccountSuggestion: true,
        item: lineItem.item,
        catalogItemId: lineItem.catalogItemId,
        catalogItemPriceId: lineItem.catalogItemPriceId,
    };
}

const SERVER_REVIEW_ERRORS: ImporterErrors[] = [
    ImporterErrors.ACCRUAL_WRONG_WORKSPACE,
    ImporterErrors.ACCRUAL_UNREADABLE_FILE,
    ImporterErrors.ACCRUAL_NOT_ACCRUAL_DOCUMENT,
    ImporterErrors.ACCRUAL_WRONG_DOCUMENT_TYPE,
    ImporterErrors.EXTRACTION_FAILED,
    ImporterErrors.ACCRUAL_LINE_ITEMS_DONT_SUM_TO_TOTAL,
    ImporterErrors.FOREIGN_CURRENCY,
    ImporterErrors.PERIOD_IS_LOCKED,
    ImporterErrors.ACCRUAL_POSSIBLE_DUPLICATE,
    ImporterErrors.ACCRUAL_PARTIALLY_PAID,
];

const SKIPPABLE_REVIEW_ERRORS: ImporterErrors[] = [
    ImporterErrors.ACCRUAL_NOT_ACCRUAL_DOCUMENT,
    ImporterErrors.ACCRUAL_WRONG_DOCUMENT_TYPE,
];

const CONFIRM_BLOCKING_REVIEW_ERRORS: ImporterErrors[] = [
    ImporterErrors.FOREIGN_CURRENCY,
    ImporterErrors.PERIOD_IS_LOCKED,
];

function reviewTaskMessages({
    missingFields,
    dueDateInvalid,
    customerLabel,
    confirmSuggestedLabel,
    hasSuggestedCustomer,
    numberLabel,
    unmatchedEntityName,
}: {
    missingFields: AccrualDraftRequiredField[];
    dueDateInvalid: boolean;
    customerLabel: string;
    confirmSuggestedLabel: string;
    hasSuggestedCustomer: boolean;
    numberLabel: string;
    unmatchedEntityName: string | null;
}): Array<{ id: string; message: string }> {
    const tasks: Array<{ id: string; message: string }> = missingFields.map(
        (field) => {
            if (field === "item") {
                return { id: field, message: "Add an item" };
            }
            if (field === "entity") {
                return {
                    id: field,
                    message: unmatchedEntityName
                        ? unmatchedEntityReviewTaskText(unmatchedEntityName)
                        : "Select an entity",
                };
            }
            if (field === "customer") {
                return {
                    id: field,
                    message: hasSuggestedCustomer
                        ? confirmSuggestedLabel
                        : customerLabel,
                };
            }
            if (field === "account") {
                return { id: field, message: "Select an account" };
            }
            if (field === "dueDate") {
                return { id: field, message: "Select a due date" };
            }
            return {
                id: field,
                message: `Enter ${numberLabel.toLowerCase()}`,
            };
        },
    );
    if (dueDateInvalid) {
        tasks.push({
            id: "dueDate",
            message: "Due date must be on or after the invoice date",
        });
    }
    return tasks;
}

function senderEmailFromSource(source: string): string | null {
    const parsed = optionalCounterpartyEmailSchema.safeParse(source);
    if (!parsed.success || !parsed.data) {
        return null;
    }
    return parsed.data;
}

interface Props {
    financialDocument: FinancialDocumentExtended;
    extraction: InvoiceExtractionRecord | null;
    entities: Entity[];
    entity: Entity | null;
    customer: NativeInvoiceCustomer | null;
    suggestedCustomerName?: string | null;
    mode: AccrualDocumentImporterMode;
    navigationRows: AccrualImporterDocumentRow[];
    onClose: () => void;
    onDiscard: () => void;
    onImported: () => void;
}

export const AccrualDraftDetailsForm: React.FC<Props> = ({
    financialDocument,
    extraction,
    entities,
    entity,
    customer,
    suggestedCustomerName,
    mode,
    navigationRows,
    onClose,
    onDiscard,
    onImported,
}) => {
    const copy = accrualDraftCopy(mode);
    const { revalidateDocument } = useContext(AccrualImporterContext);
    const toaster = useToaster();
    const counterpartyCreatePrefill = useMemo(
        () =>
            buildAccrualCounterpartyCreatePrefill({
                financialDocument,
                extractionId: extraction?.id ?? null,
                counterpartyName: extraction?.counterpartyName,
                mode,
            }),
        [extraction?.counterpartyName, extraction?.id, financialDocument, mode],
    );
    const initial = useMemo(() => {
        const created = createNativeInvoiceFormState({
            mode: "create",
            entity: entity ?? undefined,
        });
        return {
            raw: extraction
                ? applyInvoiceExtractionToRawState({
                      raw: created.raw,
                      extraction,
                      customer,
                  })
                : created.raw,
            lineCodingBaselineByLineId: created.lineCodingBaselineByLineId,
        };
    }, [customer, entity, extraction]);
    const [selectedEntity, setSelectedEntity] = useState<Entity | null>(entity);
    const form = useNativeInvoiceForm({
        initial,
        defaultEntity: selectedEntity ?? undefined,
    });
    const baselineFingerprint = useRef(form.dirtyFingerprint);
    const isDirty = form.dirtyFingerprint !== baselineFingerprint.current;
    const [isDiscardOpen, setIsDiscardOpen] = useState(false);
    const [isCreateCustomerOpen, setIsCreateCustomerOpen] = useState(false);
    const [activeReviewTaskId, setActiveReviewTaskId] = useState<string>();
    const { defaultLayout, onLayoutChanged } = useDefaultLayout({
        groupId: "accrual-draft-review-panel-group",
        storage: localStorage,
    });
    const { download, isDownloading } =
        useDownloadFinancialDocumentFile(financialDocument);
    const { confirm, confirmAndClose, isConfirming } = useConfirmAccrualDraft({
        financialDocument,
        extraction,
        state: form.state,
        totalAmount: form.total,
        taxAmount: form.taxAmount,
        discountAmount: form.discountAmount,
        depositAmount: form.depositAmount,
        entityId: selectedEntity?.id ?? null,
        onImported,
        onClose,
    });
    const saveDetails = useSaveAccrualDraftDetails({
        financialDocumentId: financialDocument.id,
        extractionId: extraction?.id ?? null,
    });
    const saveCustomer = useSaveAccrualDraftCustomer(saveDetails);
    const getEntitySwitchAccounts = useEntitySwitchAccounts();
    const [isSwitchingEntity, setIsSwitchingEntity] = useState(false);
    const savedEntityIdRef = useRef(extraction?.entityId ?? null);
    const requestedDocumentNumberRef = useRef<string | null | undefined>(
        extraction?.documentNumber ?? null,
    );
    const assignToMatchingDrafts = useAssignCounterpartyToMatchingDrafts();
    const customerId = form.state.customer?.id ?? "";
    const counterpartyDetailChanges = useAccrualCounterpartyDetailChanges({
        financialDocument,
        extractionId: extraction?.id ?? null,
        counterpartyId:
            form.state.customer?.id ?? extraction?.counterpartyId ?? null,
        mode,
        onCounterpartyLocalized: saveCustomer,
    });
    const suggestedAccountLines = (extraction?.lineItems ?? []).flatMap(
        (lineItem, rowIndex) => {
            const formLine = form.state.lines.find(
                (line) => line.id === lineItem.lineId,
            );
            const isSuggestedAccountKept =
                formLine === undefined ||
                (formLine.catalogItemId === null &&
                    formLine.accrualAccountId === lineItem.accountId);
            return isAccrualAccountSuggestion(lineItem.accountIdSource) &&
                isSuggestedAccountKept
                ? [{ lineItem, rowIndex }]
                : [];
        },
    );
    const confirmAccountSuggestions = useCallback(
        (lineIds: readonly string[]) => {
            if (!extraction || lineIds.length === 0) {
                return;
            }
            const upserts = suggestedAccountLines
                .filter(({ lineItem }) => lineIds.includes(lineItem.lineId))
                .map(({ lineItem, rowIndex }) =>
                    accountSuggestionUpsert({
                        lineItem,
                        rowIndex,
                        formLines: form.state.lines,
                    }),
                );
            if (upserts.length === 0) {
                return;
            }
            const request =
                mode === "bills"
                    ? batchUpdateBillExtractionRows({
                          financialDocumentId: financialDocument.id,
                          billExtractionId: extraction.id,
                          upserts,
                          deletes: [],
                      })
                    : batchUpdateInvoiceExtractionRows({
                          financialDocumentId: financialDocument.id,
                          invoiceExtractionId: extraction.id,
                          upserts,
                          deletes: [],
                      });
            void request
                .then(() => {
                    revalidateDocument({
                        financialDocumentId: financialDocument.id,
                    });
                })
                .catch(() => {
                    toaster.error("Failed to confirm the suggested account");
                });
        },
        [
            extraction,
            financialDocument.id,
            form.state.lines,
            mode,
            revalidateDocument,
            suggestedAccountLines,
            toaster,
        ],
    );
    const {
        suggestedItemLineIds,
        confirmItemSuggestions,
        tasks: itemSuggestionTasks,
    } = useCatalogItemSuggestions({
        entityId: mode === "invoices" ? selectedEntity?.id : undefined,
        lines: form.state.lines,
        updateLine: form.updateLine,
    });
    const dueDateInvalid =
        getNativeInvoiceDueDateError(form.state) !== undefined;
    const missingFields = accrualDraftMissingFields({
        entityId: selectedEntity?.id ?? null,
        customerId,
        invoiceNumber: form.state.invoiceNumber,
        dueDate: form.state.dueDate,
        lines: form.state.lines,
    });
    const unmatchedEntityName = getUnmatchedParsedEntityName({
        financialDocument,
        extractionId: extraction?.id,
        mode,
        entities,
    });
    const hasSuggestedCustomer = Boolean(suggestedCustomerName);
    const formReviewTasks = reviewTaskMessages({
        missingFields,
        dueDateInvalid,
        customerLabel: copy.customerPlaceholder,
        confirmSuggestedLabel: copy.confirmSuggestedLabel,
        hasSuggestedCustomer,
        numberLabel: copy.numberLabel,
        unmatchedEntityName,
    });
    const serverReviewTasks = (financialDocument.importerErrors ?? [])
        .filter((error) => SERVER_REVIEW_ERRORS.includes(error))
        .map((error) => ({
            id: error,
            message: getDisplayErrorMessage(
                { kind: ImporterDisplayErrorKind.IMPORTER_EXTRACTION, error },
                financialDocument,
            ),
        }));
    const switchEntity = async (nextEntity: Entity) => {
        const accounts = await getEntitySwitchAccounts({
            fromEntityId: selectedEntity?.id ?? null,
            toEntityId: nextEntity.id,
        }).catch(() => null);
        if (!accounts) {
            toaster.error("Failed to load accounts for entity");
            return;
        }
        if (nextEntity.id !== savedEntityIdRef.current) {
            const isSaved = await saveDetails({
                details: { entityId: nextEntity.id },
                errorMessage: "Failed to update entity",
            });
            if (!isSaved) {
                return;
            }
            savedEntityIdRef.current = nextEntity.id;
        }
        setSelectedEntity(nextEntity);
        form.setState((prev) => ({
            ...prev,
            from: fromStateFromEntity({ entity: nextEntity }),
            lines: remapLineAccountsToEntity({
                lines: prev.lines,
                ...accounts,
            }),
        }));
    };

    const handleSelectEntity = (nextEntity: Entity | undefined) => {
        if (
            !nextEntity ||
            isSwitchingEntity ||
            nextEntity.id === selectedEntity?.id
        ) {
            return;
        }
        setIsSwitchingEntity(true);
        void switchEntity(nextEntity).finally(() =>
            setIsSwitchingEntity(false),
        );
    };

    const handleDocumentNumberBlur = () => {
        const trimmedNumber = form.state.invoiceNumber.trim();
        const documentNumber = trimmedNumber === "" ? null : trimmedNumber;
        if (documentNumber === requestedDocumentNumberRef.current) {
            return;
        }
        requestedDocumentNumberRef.current = documentNumber;
        void saveDetails({
            details: { documentNumber },
            errorMessage: `Failed to update ${copy.numberLabel.toLowerCase()}`,
        }).then((isSaved) => {
            if (
                !isSaved &&
                requestedDocumentNumberRef.current === documentNumber
            ) {
                requestedDocumentNumberRef.current = undefined;
            }
        });
    };

    const isWrongEntityActive =
        (financialDocument.importerErrors ?? []).includes(
            ImporterErrors.ACCRUAL_WRONG_ENTITY,
        ) &&
        !(financialDocument.dismissedImporterErrors ?? []).includes(
            ImporterErrors.ACCRUAL_WRONG_ENTITY,
        );
    const ownPartyEntity = isWrongEntityActive
        ? getSuggestedOwnPartyEntity({
              financialDocument,
              extractionId: extraction?.id,
              mode,
              entities,
          })
        : undefined;
    const suggestedEntity =
        ownPartyEntity?.id === selectedEntity?.id ? undefined : ownPartyEntity;
    const entityReviewTasks = suggestedEntity
        ? [
              {
                  id: ImporterErrors.ACCRUAL_WRONG_ENTITY,
                  message: `Switch entity to ${suggestedEntity.name}`,
                  onSelect: () => handleSelectEntity(suggestedEntity),
              },
          ]
        : [];
    const detailChangeTasks = counterpartyDetailChanges.shouldBlockConfirm
        ? [
              {
                  id: "counterparty-details",
                  message: copy.changedDetailsTask,
              },
          ]
        : [];
    const confirmTasks = [
        ...formReviewTasks,
        ...itemSuggestionTasks,
        ...detailChangeTasks,
        ...serverReviewTasks.filter((task) =>
            CONFIRM_BLOCKING_REVIEW_ERRORS.includes(task.id),
        ),
    ];
    const confirmDisabledReason =
        confirmTasks.length === 0
            ? null
            : confirmTasks.map((task) => task.message).join(". ");
    const senderEmail = senderEmailFromSource(financialDocument.source);

    const removeImporterErrorMutation =
        useRemoveImporterErrorMutation(financialDocument);

    const afterUnsavedDiscardRef = useRef(onClose);

    const runAfterUnsavedCheck = useCallback(
        (action: () => void) => {
            if (isDirty) {
                afterUnsavedDiscardRef.current = action;
                setIsDiscardOpen(true);
                return;
            }
            action();
        },
        [isDirty, setIsDiscardOpen],
    );

    const requestClose = useCallback(
        () => runAfterUnsavedCheck(onClose),
        [runAfterUnsavedCheck, onClose],
    );

    const discardDraft = useCallback(
        () => runAfterUnsavedCheck(onDiscard),
        [runAfterUnsavedCheck, onDiscard],
    );

    useKeyboardCommand({
        keys: "Escape",
        callback: () => requestClose(),
        options: {
            description: "Close invoice draft",
            enabled: !isDiscardOpen,
            preventDefault: true,
        },
        dependencies: [requestClose, isDiscardOpen],
    });

    return (
        <NativeInvoiceFormProvider state={form.state} setState={form.setState}>
            <AccrualDraftDrawerShell
                financialDocument={financialDocument}
                navigationRows={navigationRows}
                onClose={requestClose}
                guardNavigation={runAfterUnsavedCheck}
            >
                <section className={importerStyles.preview}>
                    <ResizablePanelGroup
                        orientation="horizontal"
                        defaultLayout={defaultLayout}
                        onLayoutChanged={onLayoutChanged}
                    >
                        <ResizablePanel
                            defaultSize="50%"
                            minSize="25%"
                            maxSize="75%"
                            id="accrual-draft-file-panel"
                        >
                            {financialDocument.fileName ? (
                                <FinancialDocumentPreview
                                    financialDocument={financialDocument}
                                    compact
                                    noPadding
                                />
                            ) : (
                                <div className={styles.previewPane}>
                                    <InvoiceDocumentPreview
                                        state={form.state}
                                        entity={selectedEntity ?? undefined}
                                        customerEmail={form.customerEmail}
                                        subtotal={form.subtotal}
                                        total={form.total}
                                        fitToViewportHeight
                                    />
                                </div>
                            )}
                        </ResizablePanel>
                        <ResizableHandle />
                        <ResizablePanel
                            defaultSize="50%"
                            minSize="25%"
                            maxSize="75%"
                            id="accrual-draft-data-panel"
                        >
                            <div className={importerStyles.dataPreview}>
                                <AccrualDraftDetailsSections
                                    entities={entities}
                                    selectedEntity={selectedEntity ?? undefined}
                                    isEntityLocked={isSwitchingEntity}
                                    activeReviewTaskId={activeReviewTaskId}
                                    onSelectEntity={handleSelectEntity}
                                    suggestedEntity={suggestedEntity}
                                    onDismissSuggestedEntity={() =>
                                        removeImporterErrorMutation.mutate(
                                            ImporterErrors.ACCRUAL_WRONG_ENTITY,
                                        )
                                    }
                                    onSelectCustomer={form.onSelectCustomer}
                                    subtotal={form.subtotal}
                                    total={form.total}
                                    onChangeLine={form.updateLine}
                                    onAddLine={form.addLine}
                                    onRemoveLine={form.removeLine}
                                    partySubtitle={copy.partySubtitle}
                                    customerPlaceholder={
                                        copy.customerPlaceholder
                                    }
                                    suggestedCustomerName={
                                        suggestedCustomerName
                                    }
                                    counterpartyCreatePrefill={
                                        counterpartyCreatePrefill
                                    }
                                    isCreateCustomerOpen={isCreateCustomerOpen}
                                    onCreateCustomerOpenChange={
                                        setIsCreateCustomerOpen
                                    }
                                    counterpartyDetailChanges={
                                        detailChangeTasks.length > 0 ? (
                                            <AccrualDraftCounterpartyDetailChanges
                                                financialDocument={
                                                    financialDocument
                                                }
                                                detailChanges={
                                                    counterpartyDetailChanges
                                                }
                                                copy={copy}
                                                isHighlighted={
                                                    activeReviewTaskId ===
                                                    "counterparty-details"
                                                }
                                                onCreateNew={() =>
                                                    setIsCreateCustomerOpen(
                                                        true,
                                                    )
                                                }
                                            />
                                        ) : undefined
                                    }
                                    onCustomerCreated={(counterparty) => {
                                        void saveCustomer(counterparty);
                                        void assignToMatchingDrafts({
                                            counterparty,
                                            extractedName:
                                                extraction?.counterpartyName,
                                            excludeExtractionId: extraction?.id,
                                        });
                                    }}
                                    senderEmail={senderEmail}
                                    suggestedAccountLineIds={suggestedAccountLines.map(
                                        ({ lineItem }) => lineItem.lineId,
                                    )}
                                    onConfirmAccountSuggestion={(lineId) =>
                                        confirmAccountSuggestions([lineId])
                                    }
                                    suggestedItemLineIds={suggestedItemLineIds}
                                    onConfirmItemSuggestion={(lineId) =>
                                        confirmItemSuggestions([lineId])
                                    }
                                    dateLabel={copy.dateLabel}
                                    numberLabel={copy.numberLabel}
                                    onNumberBlur={handleDocumentNumberBlur}
                                />
                            </div>
                        </ResizablePanel>
                    </ResizablePanelGroup>
                </section>
                <AccrualDraftCommandBar
                    reviewTasks={[
                        ...detailChangeTasks,
                        ...formReviewTasks.map((task) => ({
                            ...task,
                            onSelect:
                                task.id === "customer" && hasSuggestedCustomer
                                    ? () => setIsCreateCustomerOpen(true)
                                    : undefined,
                        })),
                        ...itemSuggestionTasks,
                        ...entityReviewTasks,
                        ...serverReviewTasks.map((task) => ({
                            ...task,
                            onSkip: SKIPPABLE_REVIEW_ERRORS.includes(task.id)
                                ? () =>
                                      removeImporterErrorMutation.mutate(
                                          task.id,
                                      )
                                : undefined,
                        })),
                    ]}
                    onReviewTaskChange={(task) =>
                        setActiveReviewTaskId(task?.id)
                    }
                    onDownload={() => {
                        void download();
                    }}
                    isDownloading={isDownloading}
                    onDiscard={discardDraft}
                    onConfirmSuggestions={
                        suggestedAccountLines.length > 0 ||
                        suggestedItemLineIds.length > 0
                            ? () => {
                                  confirmItemSuggestions(suggestedItemLineIds);
                                  confirmAccountSuggestions(
                                      suggestedAccountLines
                                          .map(
                                              ({ lineItem }) => lineItem.lineId,
                                          )
                                          .filter(
                                              (lineId) =>
                                                  !suggestedItemLineIds.includes(
                                                      lineId,
                                                  ),
                                          ),
                                  );
                              }
                            : undefined
                    }
                    onConfirm={confirm}
                    onConfirmAndClose={confirmAndClose}
                    isConfirming={isConfirming}
                    isConfirmDisabled={
                        isConfirming ||
                        isSwitchingEntity ||
                        confirmDisabledReason !== null
                    }
                    confirmDisabledReason={confirmDisabledReason}
                />
                <ConfirmDiscardChangesModal
                    show={isDiscardOpen}
                    isSaving={false}
                    saveDisabled
                    saveDisabledTooltip={copy.discardTooltip}
                    onSave={() => undefined}
                    onDiscard={() => {
                        setIsDiscardOpen(false);
                        afterUnsavedDiscardRef.current();
                    }}
                    onCancel={() => setIsDiscardOpen(false)}
                />
            </AccrualDraftDrawerShell>
        </NativeInvoiceFormProvider>
    );
};
